import { FC, useState } from 'react';

import { useLocation } from 'react-router-dom';

import { usePageTitle } from 'hooks/usePageTitle';

import { Button } from 'components/parts/Button';
import { PaginationButton } from 'components/parts/PaginationButton';
import { SkipButton } from 'components/parts/SkipButton';

import styles from './WalkThrough.module.scss';

import sources from 'data/sources.json';

type WalkThroughProps = {
  handleClose: () => void;
};

const WalkThroughStep = ({
  stepIndex,
  numSteps,
  prevStep,
  nextStep,
  lead,
  imageUrl,
  contentText,
}: {
  stepIndex: number;
  numSteps: number;
  prevStep: () => void;
  nextStep: () => void;
  lead: string;
  imageUrl: string;
  contentText: string;
}) => {
  return (
    <article className={styles.article}>
      <div className={styles.content}>
        <header className={styles.header}>
          <h1 className={styles.heading}>楽しみ方ガイド</h1>
        </header>
        <div className={styles.body}>
          <p className={styles.lead}>
            {lead.split('\\n').map((line, index) => (
              <span key={index}>{line}</span>
            ))}
          </p>
          <div className={styles.imageWrapper}>
            <img src={imageUrl} alt="" className={styles.image} />
          </div>
          <p className={styles.paragraph}>{contentText}</p>
        </div>
        <div className={styles.foot}>
          <div className={styles.pagination}>
            <PaginationButton onClick={prevStep} variant="prev" disabled={stepIndex === 0} />
            <span className={styles.pageNumber}>
              {stepIndex + 1}/{numSteps}
            </span>
            <PaginationButton onClick={nextStep} disabled={stepIndex === numSteps - 1} />
          </div>
        </div>
      </div>
    </article>
  );
};

type WalkThroughStepItem = {
  header: string;
  image: string;
  contentText: string;
};

const walkThroughStepItems: WalkThroughStepItem[] = sources.playGuideSteps.map(step => ({
  header: step.header,
  image: step.image.url,
  contentText: step.contentText,
}));

export const WalkThrough: FC<WalkThroughProps> = ({ handleClose }) => {
  usePageTitle('楽しみ方ガイド');
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const redirect = queryParams.get('redirect') || false;
  const completeButtonText = redirect === 'menu' ? 'OK' : '次へ';

  const numSteps = walkThroughStepItems.length;
  const [stepIndex, setStepIndex] = useState<number>(0);

  const shiftStep = (dir: -1 | 1) => setStepIndex(prev => prev + dir);
  const completeSteps = () => handleClose();

  const currentStepItem = walkThroughStepItems[stepIndex];

  return (
    <div className={styles.wrapper}>
      <article className={styles.contentRoot}>
        <WalkThroughStep
          stepIndex={stepIndex}
          numSteps={numSteps}
          prevStep={() => shiftStep(-1)}
          nextStep={() => shiftStep(1)}
          lead={currentStepItem.header}
          imageUrl={currentStepItem.image}
          contentText={currentStepItem.contentText}
        />
      </article>
      {stepIndex === numSteps - 1 && (
        <div className={styles.footer}>
          <Button text={completeButtonText} handleClick={completeSteps} />
        </div>
      )}
      <div className={styles.screen}></div>
      <div className={styles.screenSkip}>
        <SkipButton onClick={handleClose} variant="secondary" />
      </div>
    </div>
  );
};
